import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/cinema";

export default function HeroCarousel({ movies }: { movies: Movie[] }) {
  const [index, setIndex] = useState(0);
  const touchStart = useRef(0);

  useEffect(() => {
    if (movies.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((v) => (v + 1) % movies.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [movies.length]);

  if (movies.length === 0) return null;

  return (
    <div
      className="relative h-[320px] overflow-hidden rounded-lg bg-primary-950 md:h-[460px]"
      onTouchStart={(e) => {
        touchStart.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        const diff = e.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(diff) < 50) return;
        setIndex((v) =>
          diff < 0 ? (v + 1) % movies.length : (v - 1 + movies.length) % movies.length,
        );
      }}
    >
      {movies.map((movie, i) => (
        <Link
          key={movie.id}
          to={`/movie/${movie.id}`}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title} 焦点海报`}
            title={`${movie.title} 九七电影院`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/40 to-primary-950/10"></div>
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
            <span className="mb-2 inline-flex items-center gap-1 rounded-full bg-accent-500 px-2.5 py-1 text-xs font-medium text-background-50">
              <i className="ri-fire-fill"></i>
              正在热映
            </span>
            <h2 className="font-heading text-2xl font-bold text-background-50 md:text-4xl">
              {movie.title}
            </h2>
            <p className="mt-2 max-w-xl text-sm text-background-100/90 line-clamp-2">
              {movie.synopsis}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-background-100/90">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-400"></i>
                {movie.rating.toFixed(1)}
              </span>
              <span>{movie.genres.join(" · ")}</span>
              <span>{movie.duration} 分钟</span>
              <span className="hidden sm:inline">{movie.releaseDate} 上映</span>
            </div>
          </div>
        </Link>
      ))}

      <button
        type="button"
        onClick={() => setIndex((v) => (v - 1 + movies.length) % movies.length)}
        className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-background-50 transition-colors hover:bg-primary-950/80 md:left-4 md:h-10 md:w-10"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => setIndex((v) => (v + 1) % movies.length)}
        className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-background-50 transition-colors hover:bg-primary-950/80 md:right-4 md:h-10 md:w-10"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-4 right-5 flex items-center gap-1.5 md:right-8">
        {movies.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`切换到 ${movie.title}`}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-6 bg-accent-500" : "w-1.5 bg-background-50/60"
            }`}
          ></button>
        ))}
      </div>
    </div>
  );
}